import React, {memo, MutableRefObject, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {TabData} from '../types';
import styles from './styles.module.scss';
import {isRTL} from '../../../../../Localization';
import {eventPrevented} from '../../../../../utils/eventPrevented';
import {keys} from '../../../../../constants/KeyCodes';

import {
    DropdownV2,
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    Text
} from '@priority-software/priority-style-react';
import {texts} from '../texts';
import {scrollSelectedItemIntoView} from '../../../../../hooks/dropdown/useScroll';

type DropdownItemProps = {
    index: number;
    item: TabData;
    isSelected: boolean;
    isFocused: boolean;
    onSelect: (val: string) => void;
    elRef: (el: MutableRefObject<HTMLDivElement>, index: number) => void;
};

type HiddenTabsDropdownProps = {
    onChange: (e) => void;
    onContextMenu: (e) => void;
    activeId: string;
    componentClasses?: string;
    data: Array<TabData>;
    maxItemsHeight?: number;
};

const dropdownContentStyles = isRTL()
    ? {top: 30, left: 0, right: 'auto'}
    : {top: 30, right: 0, left: 'auto'};

const defaultFocusIndex = 0;
const pageNumber = 6;

export const HiddenTabsDropdown = (props: HiddenTabsDropdownProps) => {
    const {data, activeId, onChange, maxItemsHeight = 210} = props;
    const [open, setOpen] = useState(false);
    const [focusedItem, setFocusedItem] = useState(defaultFocusIndex);
    const [searchValue, setSearchValue] = useState('');
    const prevFocusedIndex = useRef(defaultFocusIndex);
    const itemsRef = useRef({});

    const itemsData =
        searchValue !== ''
            ? data.filter(item => item.title.toLowerCase().includes(searchValue?.toLowerCase()))
            : data;

    const openDropdown = e => {
        eventPrevented(e, true);
        setOpen(true);
        setFocusedItem(
            Math.max(
                data.findIndex(item => item.id === activeId),
                0
            )
        );
    };

    const closeDropdown = useCallback(() => {
        setOpen(false);
        setSearchValue('');
    }, []);

    const onSelect = useCallback(
        (value: string) => {
            onChange(value);
            closeDropdown();
        },
        [closeDropdown, onChange]
    );

    const handleInputChange = useCallback(e => {
        itemsRef.current = {}; // reset refs;
        setSearchValue(e.target.value);
        setFocusedItem(0);
    }, []);

    const getItemRef = useCallback((el: MutableRefObject<HTMLDivElement>, index: number) => {
        itemsRef.current[index] = el;
    }, []);

    const onKeydown = e => {
        const lastIndex = data.length - 1;

        switch (e.key) {
            case keys.ENTER:
                eventPrevented(e, true);
                const currentData = itemsData[focusedItem];
                if (currentData) {
                    onSelect(itemsData[focusedItem].id);
                }
                break;
            case keys.ARROW_DOWN:
                eventPrevented(e, true);
                const nextIndex = focusedItem + 1;
                setFocusedItem(nextIndex < lastIndex ? nextIndex : lastIndex);
                break;
            case keys.ARROW_UP:
                eventPrevented(e, true);
                const prevIndex = focusedItem - 1;
                setFocusedItem(prevIndex > 0 ? prevIndex : 0);
                break;
            case keys.HOME:
                eventPrevented(e, true);
                setFocusedItem(0);
                break;
            case keys.END:
                eventPrevented(e, true);
                setFocusedItem(lastIndex);
                break;
            case keys.PAGE_DOWN:
                eventPrevented(e, true);
                setFocusedItem(Math.min(focusedItem + pageNumber, lastIndex));
                break;
            case keys.PAGE_UP:
                eventPrevented(e, true);
                setFocusedItem(Math.max(0, focusedItem - pageNumber));
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (focusedItem !== prevFocusedIndex.current) {
            scrollSelectedItemIntoView(itemsRef.current[focusedItem]);
            prevFocusedIndex.current = focusedItem;
        }
    }, [focusedItem]);

    return (
        <div className={cx(styles.dropdown, props.componentClasses)}>
            <DropdownV2
                isOpen={open}
                trigger={
                    <>
                        <div
                            onClick={open ? closeDropdown : openDropdown}
                            className={cx(styles.trigger, styles.dropdownToggle)}
                        >
                            <Icon icon={PriorityIcon.arrowBottom} componentClasses={styles.icon} />
                        </div>
                    </>
                }
                onClose={closeDropdown}
                onKeyDown={onKeydown}
                eventTypes={['touchstart', 'mousedown']}
                contentStyle={dropdownContentStyles}
                componentClasses={styles.dropdown}
                contentComponentClasses={styles.dropdownMenu}
            >
                <Input
                    autoFocus
                    value={searchValue}
                    onChange={handleInputChange}
                    componentClasses={styles.input}
                />

                <Scrollbar autoHeight autoHeightMax={maxItemsHeight}>
                    {itemsData.map((item, index) => {
                        return (
                            <DropdownItem
                                key={item.id}
                                index={index}
                                elRef={getItemRef}
                                item={item}
                                onSelect={onSelect}
                                isSelected={item.id === activeId}
                                isFocused={index === focusedItem}
                            />
                        );
                    })}

                    {itemsData.length === 0 && (
                        <Text textColor="primary-700" skin="paragraph-2">
                            {texts.noResults}
                        </Text>
                    )}
                </Scrollbar>
            </DropdownV2>
        </div>
    );
};

const DropdownItem = memo((props: DropdownItemProps) => {
    const {item, isSelected, isFocused, onSelect, elRef, index} = props;
    const itemRef = useRef<HTMLDivElement>(null);

    const handleClick = () => onSelect(item.id);

    useEffect(() => {
        elRef(itemRef, index);
    }, [elRef, index]);

    return (
        <div
            ref={itemRef}
            tabIndex={0}
            onClick={handleClick}
            className={cx(styles.item, {
                [styles.selected]: isSelected,
                [styles.isFocused]: isFocused
            })}
        >
            {item.title}
        </div>
    );
});
